// src/lib/invoice.js
//
// Builds the customer-facing invoice: the email subject/body, and the numbers
// the printed copy shows. Pure functions — no React, no Firestore — so the
// wording and the arithmetic can be tested directly.

import { neededDateOf, neededTimeOf, fmtDay } from './priority';

const money = (n) => '$' + (Number(n) || 0).toFixed(2);

export function invoiceNumberOf(order) {
  return `INV-${order?.orderNumber ?? '—'}`;
}

export function invoiceDateOf(order) {
  const ts = order?.createdAt;
  const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : new Date();
  return d;
}

export function fmtLongDate(d) {
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** Best email we already hold for this order, or '' so the UI can ask. */
export function emailForOrder(order) {
  return order?.billing?.email || order?.customerEmail || '';
}

export function customerNameOf(order) {
  const b = order?.billing || {};
  return `${b.firstName || ''} ${b.lastName || ''}`.trim() || order?.customerName || 'Customer';
}

/** Line items with their extended price resolved. */
export function invoiceLines(order) {
  return (order?.items || [])
    .filter(i => i.description)
    .map(i => {
      const qty = Number(i.qty) || 0;
      const price = Number(i.price) || 0;
      const disc = Number(i.discount) || 0;
      return { qty, description: i.description, price, discount: disc, ext: qty * price * (1 - disc / 100) };
    });
}

/**
 * Totals for display. These come off the stored order rather than being
 * recomputed, so the invoice always matches what the customer was charged —
 * even if the shop's fees or tax rate have changed since.
 */
export function invoiceTotals(order) {
  return {
    subtotal:    Number(order?.subtotal)    || 0,
    deliveryFee: Number(order?.deliveryFee) || 0,
    serviceFee:  Number(order?.serviceFee)  || 0,
    tax:         Number(order?.tax)         || 0,
    total:       Number(order?.total)       || 0,
  };
}

const PAID_STATUSES = ['paid', 'completed'];

/**
 * What we can honestly say about payment. The app records a status, not a
 * settled balance, so this reports the status rather than asserting a receipt.
 */
export function paymentSummary(order) {
  const method = order?.billing?.method || '';
  const last4  = order?.billing?.cardLast4 || '';
  const brand  = order?.billing?.cardBrand || '';
  // An explicit Paid/Not paid choice on the order wins over the inferred
  // status. Orders taken before that toggle existed have no flag, and fall
  // back to reading it from the order status as before.
  const settled = typeof order?.billing?.paid === 'boolean'
    ? order.billing.paid
    : PAID_STATUSES.includes(order?.status);
  const card = last4 ? `${brand || 'Card'} ending ${last4}` : '';
  return {
    settled,
    label: settled ? 'PAID' : 'BALANCE DUE',
    // A method is only reported once money has actually changed hands.
    // `billing.method` defaults to 'Credit Card' on a blank form, so an unpaid
    // order would otherwise print "Method(s) of Payment: Credit Card $63.00" —
    // reading as though $63 had been taken by card when nothing was collected.
    method: settled ? [method, card].filter(Boolean).join(' — ') : '',
    // What the customer intends to pay with, where that's worth showing. Never
    // presented as a completed payment.
    intendedMethod: settled ? '' : method,
    amountDue: settled ? 0 : (Number(order?.total) || 0),
  };
}

/**
 * Cancellation state, including whether the shop still owes money back.
 *
 * Cancelling a paid order leaves the customer's money with the shop. Nothing
 * used to record that, so a cancelled ticket printed "VOID — DO NOT MAKE" and
 * "PAID — Visa ending 1111" with no hint a refund was owed.
 *
 * The stored status stays 'void' so orders cancelled before this existed still
 * read correctly; everything user-facing says "cancelled", which is the word
 * the shop actually uses.
 */
export function cancellationOf(order) {
  const cancelled = order?.status === 'void';
  const c = order?.cancellation || {};
  const refunded = !!c.refundedAt;

  // Whether the order had been paid is captured AT cancel time: cancelling
  // overwrites the status with 'void', so afterwards a 'completed' order that
  // carried no explicit paid flag would look unpaid and the refund would be
  // silently dropped.
  const wasPaid = typeof c.wasPaid === 'boolean' ? c.wasPaid : order?.billing?.paid === true;

  // The operator's explicit choice wins — they may know a refund was handled
  // at the till, or that a deposit is being kept. Orders cancelled before any
  // of this existed fall back to "paid means owed".
  const owed = typeof c.refundDue === 'boolean' ? c.refundDue : wasPaid;

  return {
    cancelled,
    reason:   c.reason || '',
    at:       c.at || null,
    by:       c.by || '',
    wasPaid,
    refunded,
    // Money still sitting with the shop that belongs to the customer.
    refundDue:    cancelled && owed && !refunded,
    refundAmount: Number(order?.total) || 0,
  };
}

export function recipientBlock(order) {
  const r = order?.recipient || {};
  const name = `${r.firstName || ''} ${r.lastName || ''}`.trim();
  const cityLine = [r.city, r.stPr].filter(Boolean).join(', ');
  return [name, r.address, [cityLine, r.zipPostal].filter(Boolean).join(' '), r.phone]
    .map(s => (s || '').trim())
    .filter(Boolean);
}

export function invoiceSubject(order, shop) {
  return `Invoice ${invoiceNumberOf(order)} from ${shop?.name || 'our shop'}`;
}

/**
 * Plain-text invoice for the email body. Kept compact on purpose: it travels
 * in a mailto: URL, and some mail clients truncate long ones.
 */
export function invoiceEmailBody(order, shop) {
  const lines = invoiceLines(order);
  const tot   = invoiceTotals(order);
  const pay   = paymentSummary(order);
  const needed = neededDateOf(order);
  const time   = neededTimeOf(order);
  const rec    = recipientBlock(order);

  const out = [];
  out.push(`Hello ${customerNameOf(order)},`);
  out.push('');
  out.push(`Thank you for your order from ${shop?.name || 'our shop'}.`);
  out.push('');
  out.push(`${invoiceNumberOf(order)}`);
  out.push(`Date: ${fmtLongDate(invoiceDateOf(order))}`);
  if (needed) out.push(`Needed: ${fmtDay(needed, { withYear: true })}${time ? ` at ${time}` : ''}`);
  out.push('');
  out.push('ITEMS');
  for (const l of lines) {
    out.push(`  ${l.qty} x ${l.description}  ${money(l.ext)}`);
  }
  if (!lines.length) out.push('  (no items listed)');
  out.push('');
  out.push(`Subtotal: ${money(tot.subtotal)}`);
  if (tot.deliveryFee) out.push(`Delivery: ${money(tot.deliveryFee)}`);
  if (tot.serviceFee)  out.push(`Service:  ${money(tot.serviceFee)}`);
  out.push(`Tax:      ${money(tot.tax)}`);
  out.push(`TOTAL:    ${money(tot.total)}`);
  out.push('');
  out.push(pay.method ? `Payment: ${pay.method} (${pay.label})` : `Payment: ${pay.label}`);
  if (!pay.settled) out.push(`Amount due: ${money(pay.amountDue)}`);
  if (rec.length) {
    out.push('');
    out.push('Delivering to:');
    rec.forEach(l => out.push(`  ${l}`));
  }
  out.push('');
  if (shop?.phone) out.push(`Questions? Call us at ${shop.phone}.`);
  out.push('');
  out.push(shop?.name || '');
  if (shop?.address) out.push(shop.address);
  const cityLine = [shop?.city, shop?.province].filter(Boolean).join(', ');
  if (cityLine) out.push(cityLine);

  return out.join('\n');
}

/**
 * mailto: link. There is no mail server wired into this app, so sending opens
 * the machine's own mail client with everything filled in and the shop presses
 * Send — no SMTP credentials to manage, and the sent copy lands in their
 * regular Sent folder.
 */
export function mailtoLink({ to, subject, body }) {
  const q = new URLSearchParams();
  if (subject) q.set('subject', subject);
  if (body)    q.set('body', body);
  // URLSearchParams encodes spaces as '+', which mail clients show literally.
  const qs = q.toString().replace(/\+/g, '%20');
  return `mailto:${encodeURIComponent(to || '').replace(/%40/g, '@')}?${qs}`;
}

export function isValidEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s || '').trim());
}
